import { createRateLimitedRequest, type RequestOptions, watchRequest } from '@services/api/utils'
import { type WatchRequestConfig } from '@services/api/declarations'

import { useApiUserStore } from '@services/User/store'

import {
  type PostUserPasswordResetRequest,
  type PostUserPasswordResetValidateRequest,
  type PostUserPasswordResetConfirmRequest,
  type PatchUserChangePasswordRequest,
  type UserStoreShape,
  type PostUserRegisterRequest,
  type PostCreateProfileRequest,
  type GetOrphanedManagersRequest,
  type PostConfirmEmailRequest,
  type PostValidateEmailRequest,
  type ApiUserChangePassword,
  type OrphanedManager,
  type RegisterUserResponse,
  type UserProfile,
  PatchUserChangePasswordRequestSchema,
  PostUserPasswordResetRequestSchema,
  PostUserPasswordResetValidateRequestSchema,
  PostUserPasswordResetConfirmRequestSchema,
  PostUserRegisterRequestSchema,
  PostCreateProfileRequestSchema,
  GetOrphanedManagersRequestSchema,
  RegisterUserResponseSchema,
  OrphanedManagerSchema,
  ApiUserChangePasswordSchema,
  UserProfileSchema,
  PostValidateEmailRequestSchema,
  PostConfirmEmailRequestSchema,
} from '@services/User/declarations'

import {
  postUserPasswordResetConfirmConfig,
  postUserPasswordResetValidateConfig,
  postUserPasswordResetRequestConfig,
  patchUserChangePasswordConfig,
  getUserProfileConfig,
  postUserRegisterConfig,
  postCreateProfileConfig,
  getOrphanedManagersConfig,
  postConfirmEmailConfig,
  postValidateEmailConfig,
} from '@services/User/api'

// ***
// COMMON
// ***

const serviceName = 'User'

// ***
// REQUESTS
// ***

export function useApiUserStoreRequest() {
  const apiUserStore = useApiUserStore()
  const { sendRequest } = apiUserStore

  // GET USER PROFILE
  function fetchGetUserProfile() {
    createRateLimitedRequest<undefined>({
      sendRequest,
      endpoint: getUserProfileConfig,
      requestConfig: undefined,
      serviceName,
      saveToStore: true,
    })
  }

  function watchGetUserProfile(
    { onRequest, onSuccess, onError }: WatchRequestConfig<UserProfile>,
  ) {
    const { closeSubscription } = watchRequest<UserProfile>(getUserProfileConfig, {
      onRequest,
      onSuccess: (data) => onSuccess?.(UserProfileSchema.parse(data)),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // PATCH USER CHANGE PASSWORD
  function fetchPatchUserChangePassword(
    requestConfig: PatchUserChangePasswordRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PatchUserChangePasswordRequest>({
      sendRequest,
      endpoint: patchUserChangePasswordConfig,
      requestConfig: PatchUserChangePasswordRequestSchema.parse(requestConfig),
      serviceName,
      ...options,
    })
  }

  function watchPatchUserChangePassword(
    { onSuccess, onError }: WatchRequestConfig<ApiUserChangePassword>,
  ) {
    const { closeSubscription } = watchRequest<ApiUserChangePassword>(patchUserChangePasswordConfig, {
      onSuccess: (data) => onSuccess?.(ApiUserChangePasswordSchema.parse(data)),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST USER PASSWORD RESET CONFIRM
  function fetchPostUserPasswordResetConfirm(
    requestConfig: PostUserPasswordResetConfirmRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostUserPasswordResetConfirmRequest>({
      sendRequest,
      endpoint: postUserPasswordResetConfirmConfig,
      requestConfig: {
        ...PostUserPasswordResetConfirmRequestSchema.parse(requestConfig),
        skipToken: true,
      },
      serviceName,
      ...options,
    })
  }

  function watchPostUserPasswordResetConfirm(
    { onSuccess, onError }: WatchRequestConfig<RegisterUserResponse>,
  ) {
    const { closeSubscription } = watchRequest<RegisterUserResponse>(postUserPasswordResetConfirmConfig, {
      onSuccess: (data) => onSuccess?.(RegisterUserResponseSchema.parse(data)),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST USER PASSWORD RESET REQUEST
  function fetchPostUserPasswordResetRequest(
    requestConfig: PostUserPasswordResetRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostUserPasswordResetRequest>({
      sendRequest,
      endpoint: postUserPasswordResetRequestConfig,
      requestConfig: {
        ...PostUserPasswordResetRequestSchema.parse(requestConfig),
        skipToken: true,
      },
      serviceName,
      ...options,
    })
  }

  function watchPostUserPasswordResetRequest(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['postUserPasswordResetRequest']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['postUserPasswordResetRequest']>(postUserPasswordResetRequestConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST USER PASSWORD RESET VALIDATE
  function fetchPostUserPasswordResetValidate(
    requestConfig: PostUserPasswordResetValidateRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostUserPasswordResetValidateRequest>({
      sendRequest,
      endpoint: postUserPasswordResetValidateConfig,
      requestConfig: {
        ...PostUserPasswordResetValidateRequestSchema.parse(requestConfig),
        skipToken: true,
      },
      serviceName,
      ...options,
    })
  }

  function watchPostUserPasswordResetValidate(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['postUserPasswordResetValidate']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['postUserPasswordResetValidate']>(postUserPasswordResetValidateConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST USER REGISTER
  function fetchPostUserRegister(
    requestConfig: PostUserRegisterRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostUserRegisterRequest>({
      sendRequest,
      endpoint: postUserRegisterConfig,
      requestConfig: {
        ...PostUserRegisterRequestSchema.parse(requestConfig),
        skipToken: true,
      },
      serviceName,
      ...options,
    })
  }

  function watchPostUserRegister(
    { onSuccess, onError }: WatchRequestConfig<RegisterUserResponse>,
  ) {
    const { closeSubscription } = watchRequest<RegisterUserResponse>(postUserRegisterConfig, {
      onSuccess: (data) => onSuccess?.(RegisterUserResponseSchema.parse(data)),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // POST CREATE PROFILE
  function fetchPostCreateProfile(
    requestConfig: PostCreateProfileRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostCreateProfileRequest>({
      sendRequest,
      endpoint: postCreateProfileConfig,
      requestConfig: PostCreateProfileRequestSchema.parse(requestConfig),
      serviceName,
      ...options,
    })
  }

  function watchPostCreateProfile(
    { onSuccess, onError }: WatchRequestConfig<unknown>,
  ) {
    const { closeSubscription } = watchRequest<unknown>(postCreateProfileConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  // GET ORPHANED MANAGERS
  function fetchGetOrphanedManagers(
    requestConfig: GetOrphanedManagersRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<GetOrphanedManagersRequest>({
      sendRequest,
      endpoint: getOrphanedManagersConfig,
      requestConfig: GetOrphanedManagersRequestSchema.parse(requestConfig),
      serviceName,
      ...options,
    })
  }

  function watchGetOrphanedManagers(
    { onSuccess, onError }: WatchRequestConfig<OrphanedManager>,
  ) {
    const { closeSubscription } = watchRequest<OrphanedManager>(getOrphanedManagersConfig, {
      onSuccess: (data) => onSuccess?.(OrphanedManagerSchema.parse(data)),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  function fetchPostConfirmEmail(
    requestConfig: PostConfirmEmailRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostConfirmEmailRequest>({
      sendRequest,
      endpoint: postConfirmEmailConfig,
      requestConfig: {
        ...PostConfirmEmailRequestSchema.parse(requestConfig),
        skipToken: true,
      },
      serviceName,
      ...options,
    })
  }

  function watchPostConfirmEmail(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['postConfirmEmail']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['postConfirmEmail']>(postConfirmEmailConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  function fetchPostValidateEmail(
    requestConfig: PostValidateEmailRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<PostValidateEmailRequest>({
      sendRequest,
      endpoint: postValidateEmailConfig,
      requestConfig: PostValidateEmailRequestSchema.parse(requestConfig),
      serviceName,
      ...options,
    })
  }

  function watchPostValidateEmail(
    { onSuccess, onError }: WatchRequestConfig<UserStoreShape['postValidateEmail']>,
  ) {
    const { closeSubscription } = watchRequest<UserStoreShape['postValidateEmail']>(postValidateEmailConfig, {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    })

    return { closeSubscription }
  }

  return {
    store: apiUserStore,
    fetchGetUserProfile,
    fetchPatchUserChangePassword,
    fetchPostUserPasswordResetConfirm,
    fetchPostUserPasswordResetRequest,
    fetchPostUserPasswordResetValidate,
    fetchPostUserRegister,
    fetchPostCreateProfile,
    fetchGetOrphanedManagers,
    fetchPostConfirmEmail,
    fetchPostValidateEmail,
    // watchers
    watchGetUserProfile,
    watchPostUserRegister,
    watchPatchUserChangePassword,
    watchPostUserPasswordResetConfirm,
    watchPostUserPasswordResetRequest,
    watchPostUserPasswordResetValidate,
    watchPostCreateProfile,
    watchGetOrphanedManagers,
    watchPostConfirmEmail,
    watchPostValidateEmail,
  }
}
